const { UserService } = require('@nuskin/ns-account');
const {
    StringService,
    RunConfigService,
    storage
} = require('@nuskin/ns-util');
const axios = require('axios').default;
const { getPartialConfig } = require('@nuskin/configuration-sdk')
const { productLib } = require('@nuskin/product-lib')

const EXCLUSIVE_OFFER_API_NAME = 'exclusive-offer'
const QUALIFICATION_URL = "/v1";
const DFLT_NOT_LOGGED_IN = "You must be logged in to purchase exclusive products.";

let qualificationApiPath;

let promiseCache = {}
const buildPromiseKey = (request) => {
    return request.url + Object.keys(request.params).map(key => key + '=>' + request.params[key]).join('&')
}

let productIdLookupMappingPromises = {};
let productIdMappings = {};
const loadIdMapping = async (identifier) => {
    const runConfig = RunConfigService.getRunConfig();
    const market = runConfig.country || '';
    let language = runConfig.language || '';
    if (language.toLowerCase() == 'in' && market.toLowerCase() == 'id') {
        language = 'id'
    }
    const productLookupCacheKey = [identifier, market].join("-");

    if (!productIdLookupMappingPromises[productLookupCacheKey] && !productIdMappings[productLookupCacheKey]) {
        productIdLookupMappingPromises[productLookupCacheKey] = productLib.getProduct({
            fromId: identifier,
            market,
            language,
            env: RunConfigService.getEnvironmentCode(),
            useContentSource: 'CS'
        }).then((result) => {
            if (result.data.bundle) {
                productIdMappings[identifier + '-' + market] = result.data.bundle.parentId;
            } else {
                productIdMappings[identifier + '-' + market] = result.data.id;
            }
            (result.data.variants || []).forEach(variant => {
                productIdMappings[variant.sku + '-' + market] = result.data.id
            })
            productIdLookupMappingPromises[productLookupCacheKey] = null
        })
    }

    if (productIdLookupMappingPromises[productLookupCacheKey]) {
        await productIdLookupMappingPromises[productLookupCacheKey];
    }
};

const mapSkuToProductId = async(sku) => {
    await loadIdMapping(sku)
    return productIdMappings[sku + '-' + RunConfigService.getRunConfig().country]
}

/*
-----------------------------
Private Methods
-----------------------------
 */

const getBaseUrl = async () => {
    if (!qualificationApiPath && !promiseCache.qualificationApiPath) {
        promiseCache.qualificationApiPath = getPartialConfig({
            Equinox_Markets: ['api_gateway_path']
        }).then(result => {
            qualificationApiPath = result.Equinox_Markets.api_gateway_path
            promiseCache.qualificationApiPath = null
        })
    }

    if (promiseCache.qualificationApiPath) {
        await promiseCache.qualificationApiPath
    }

    return qualificationApiPath.replace('{api_name}', EXCLUSIVE_OFFER_API_NAME) + QUALIFICATION_URL
}

// if run config is set, go ahead and preload the base url
if (Object.keys(RunConfigService.getRunConfig()).length > 0 && RunConfigService.getRunConfig().clientId) {
    getBaseUrl()
}

/**
 * Creates a headers object for the AWS service call.
 *
 * @returns {{Accept: string, "Content-Type": string, client_id: *, client_secret: *}}
 * @private
 */
const _getHeaders = () => {
    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': RunConfigService.getRunConfig().clientId,
        'Authorization': 'Bearer ' + JSON.parse(window.sessionStorage.getItem('oktaTokens')).accessToken
    }
};

const _getQueryParams = (params) => {

    const seParams = storage.getItem({
        key: 'seParams',
        type: 'Required',
        storageType: 'localStorage'
    })
    return {
        ignoreActive: seParams && seParams.showQualifications,
        locale: `${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}`,
        ...params
    }
};

const makeRequest = async(request, errorMessageString, unwrapResponse = true) => {
    let result;
    if (UserService.isLoggedIn()) {
        try {
            let requestPromise
            if (request.method === 'GET'){
                const promiseKey = buildPromiseKey(request)
                promiseCache[promiseKey] = promiseCache[promiseKey] || axios(request).then(result => {
                    promiseCache[promiseKey] = null
                    return result
                })
                requestPromise = promiseCache[promiseKey]
            } else {
                requestPromise = axios(request)
            }
            result = await requestPromise
            result = unwrapResponse ? result.data.qualification : result
        } catch (err) {
            console.log(err);
            throw err;
        }
    } else {
        if (!errorMessageString) {
            try {
                errorMessageString = await StringService.getString('signin', DFLT_NOT_LOGGED_IN);
            } catch (e) {
                console.error(e.message);
            }
        }
        throw Error(errorMessageString);
    }
    return result;
}

/*
------------------------------
Public Methods
------------------------------
 */

/**
 * @typedef Qualification
 * @property {}
 *
 * Gets the qualifications for the logged in user.  This call currently assumes manage mode.
 * @param {boolean} manage - manage if true returns all sharable information in addition to the qualification.  (More expensive call)
 * @returns {Promise<Object>}
 */
const getQualifications = async (manage = true) => {

    const {
        id: userId
    } = (UserService.getUser() || {})

    if (!userId) {
        return {}
    }

    const requestResult = await makeRequest({
        method: 'GET',
        url: `${await getBaseUrl()}/qualification/${userId}`,
        headers: _getHeaders(),
        params: _getQueryParams({manage})
    })
    return Object.keys(requestResult)
        .reduce((qualifications, sku) => {
            qualifications[sku] = requestResult[sku]
            return qualifications
        }, {})
};

/**
 * Marks the users qualifications as viewed
 *
 * @param skus - array of SKUs
 * @param viewShared - true if manage shared is viewed (vs qualifications viewed)
 * @return {Promise<{qualifications: null, messages: (*|Array)}>}
 */
const qualificationsViewed = async (skus = null, viewShared = false) => {
    return makeRequest({
        method: 'POST',
        url: `${await getBaseUrl()}/viewed`,
        headers: _getHeaders(),
        data: {
            customerId: UserService.getUser().id,
            allViewed: !(skus && Array.isArray(skus)),
            skus: skus
        },
        params: _getQueryParams({manage: true, viewShared: viewShared})
    })
}

/**
 * Marks the users qualifications as claimed
 *
 * @Typedef Claim
 * @property {number} quantity
 * @property {string} sku
 * @property {string} customerId - the ID of the distributor they received the qualification from
 *
 * @param {Array.<Claim>} claims
 * @returns {Promise<Object>}
 */
const claimQualifications = async (claims) => {

    return makeRequest({
        method: 'POST',
        url: `${await getBaseUrl()}/claim`,
        headers: _getHeaders(),
        data: {
            customerId: UserService.getUser().id,
            claims: claims
        },
        params: _getQueryParams({manage: true})
    })
}

/**
 * Takes back qualifications from users they were shared to
 *
 * @Typedef ReclaimFrom
 * @property {number} quantity
 * @property {string} sku
 * @property {string} customerId - the ID of the distributor they received the qualification from
 *
 * @param {Array.<ReclaimFrom>} from
 * @returns {Promise<Object>}
 */
const reclaimQualifications = async (from) => {

    return makeRequest({
        method: 'POST',
        url: `${await getBaseUrl()}/reclaim`,
        headers: _getHeaders(),
        data: {
            customerId: UserService.getUser().id,
            from: from
        },
        params: _getQueryParams({manage: true})
    })
}

/**
 * @typedef QualificationTransfer
 * @property {string} customerId
 * @property {string} sku
 * @property {number} quantity
 *
 * @param {Array.<QualificationTransfer>}
 * @returns {Promise<void>}
 */
const transferQualifications = async (transfers) => {

    return makeRequest(
        {
            method: 'POST',
            url: `${await getBaseUrl()}/transfer`,
            headers: _getHeaders(),
            data: {
                action: "transfer",
                customerId: UserService.getUser().id,
                to: transfers
            },
            params: _getQueryParams({manage: true})
        },
        await StringService.getString('logInToUseQualification', DFLT_NOT_LOGGED_IN),
        false
    )
};

const returnPartialQualifications  = async (sku, returnArray) => {
    let currentQualifications = await getQualifications();
    let transfers = [];

    //We need to iterate through the shareback list and return the number that the user doesn't want.
    returnArray.forEach((returnObj) => {
        let returningTotal = 0;
        //We need to make sure that we are not trying to transfer back more than the user gave us:
        let possibleShareBackQuantity = 0;
        currentQualifications[sku].shareBackList.forEach((shareBack) => {
            if(shareBack.customerId === returnObj.customerId){
                possibleShareBackQuantity = shareBack.quantity;
            }
        })

        //If the user is trying to share back more than was originally given, only return what was originally given.
        if(possibleShareBackQuantity < returnObj.quantity){
            returningTotal = possibleShareBackQuantity;
        } else {
            returningTotal = returnObj.quantity;
        }

        transfers.push({customerId: returnObj.customerId, sku: sku, productId: sku, quantity: returningTotal})
    })

    return await transferQualifications(transfers);
}

/**
 * Returns all the qualifications back to everyone in the sharebacklists
 *
 * @returns {Promise<void>}
 */
const returnQualifications = async (sku) => {
    let qualifications = await getQualifications();
    let transfers = [];
    // Create transfers from qualification sharebacklist
    qualifications[sku].shareBackList
        .forEach((shareBack) => transfers.push({
            customerId: shareBack.customerId,
            sku: sku,
            productId: sku,
            quantity: shareBack.quantity
        }));

    return await transferQualifications(transfers);
};

/**
 * Gets a single user qualification
 *
 * @param sku
 * @returns {Promise<*>}
 */
const getQualification = async (identifier) => {
    await loadIdMapping(identifier)
    const productId = productIdMappings[identifier + '-' + RunConfigService.getRunConfig().country]
    return (await getQualifications())[productId];
};

/**
 * Checks quantity of a qualification
 *
 * @param sku
 * @param qty
 * @returns {Promise<*>}
 */
const isQualified = async (sku, qty, cartData = []) => {
    const qualification = await getQualification(sku);
    if (!qualification) {
        return false
    }

    const skuAlreadyInCart = (cartData || []).map(cartItem => cartItem.sku).includes(sku)
    let updatedCartData = cartData
    if (skuAlreadyInCart) {
        updatedCartData.forEach(cartItem => {
            if (cartItem.sku === sku) {
                cartItem.qty = qty
            }
        })
    } else {
        updatedCartData.push({
            sku,
            qty
        })
    }
    return qualification && qualification.quantity >= (await convertCartDataToBaseSkus(updatedCartData))[qualification.productId].qty;
};

const getQualifiedQty = async (sku) =>  {
    const qualification = await getQualification(sku)
    return qualification ? qualification.quantity : 0
}

const convertCartDataToBaseSkus = async (cartContents) => {

    // get all of your product mapping data
    const skuList = [...new Set(cartContents.map(cartItem => cartItem.sku))]
    await Promise.all(skuList.map(loadIdMapping))

    // aggregate cart contents and return the result
    return cartContents
        .filter(async (cartItem) => await mapSkuToProductId(cartItem.sku))
        .reduce(async (result, cartItem) => {
            if (result.then) {
                result = await result
            }
            const { sku, qty } = cartItem;

            const productId = await mapSkuToProductId(sku);
            if (!result[productId]) {
                result[productId] = {
                    sku: productId,
                    qty: 0
                }
            }
            result[productId].qty += qty;
            return result;
        }, {});
}


const getQualificationBanner = async () => {
    const qualifications = await getQualifications()
    return Object.keys(qualifications).length > 0
}

/**
 * Set Early Access Qualification modal
 * @param value true/false
 */
const setEarlyAccessQualificationModal = (value) => {
    value ? storage.setItem(storage.metadata.VIEWED_QUALIFICATION_MODAL, value) : storage.removeItem(storage.metadata.VIEWED_QUALIFICATION_MODAL);
};

/**
 * Get Early Access Qualification modal
 */
const getEarlyAccessQualificationModal = () => {
    return storage.getItem(storage.metadata.VIEWED_QUALIFICATION_MODAL) ? true : false;
};


const qualificationService = {
    convertCartDataToBaseSkus,
    claimQualifications,
    getQualification,
    getQualifications,
    getQualifiedQty,
    isQualified,
    qualificationsViewed,
    reclaimQualifications,
    returnPartialQualifications,
    returnQualifications,
    transferQualifications,
    getQualificationBanner,
    setEarlyAccessQualificationModal,
    getEarlyAccessQualificationModal
};

module.exports = qualificationService;
